import type { Time } from "~/tools/date-time/time";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import type { Record } from "~/tools/record";
import { round } from "~/tools/round";
import { sortBy } from "~/tools/sort-by";
import { timeToPx } from "~/tools/time-to-px";
import { toArray } from "~/tools/to-array";

import { Api } from "../../common/openapi";

interface HealthSegmentBuilderParams {
	gap: number;
	minWidth: number;
}

interface HealthSegmentBuilder extends Iterable<HealthSegment> {
	next(): null | [HealthSegment, HealthSegmentBuilder];
	shift(leftPx: number, health: Api.HealthStateValue | undefined): HealthSegmentBuilder;
}

class HealthSegmentNode implements HealthSegmentBuilder {
	public constructor(
		private readonly params: HealthSegmentBuilderParams,
		private readonly tail: HealthSegmentBuilder,
		private readonly segment: HealthSegment,
	) {}

	public next(): [HealthSegment, HealthSegmentBuilder] {
		return [this.segment, this.tail];
	}

	/**
	 * Attempts to add a new segment to the LEFT side of the current segment,
	 * so it should be used in right-to-left iteration through the timeline.
	 */
	public shift(leftPx: number, health: Api.HealthStateValue | undefined): HealthSegmentBuilder {
		if (leftPx >= this.segment.leftPx) {
			return this;
		}

		const maxSegmentLeftPx = this.segment.rightPx - this.params.minWidth;

		// if the head segment is shorter than the minimal width
		if (maxSegmentLeftPx < this.segment.leftPx) {
			// the incoming segment merges to the head segment to make it at least minimal width long
			return new HealthSegmentNode(this.params, this.tail, {
				...this.segment,
				// the health is the most severe of the two
				health:
					severityDiff(this.segment.health || Api.HealthStateValue.UNKNOWN, health || Api.HealthStateValue.UNKNOWN) > 0
						? this.segment.health
						: health,
				// the head segment extends to the left edge of the incoming segment
				// but not further than the minimal width
				leftPx: max(leftPx, maxSegmentLeftPx),
			}).shift(leftPx, health); // and tries the incoming segment again
		}

		const shiftedSegmentLeftPx = min(
			// desired minimal right edge of the incoming segment
			leftPx + this.params.minWidth + this.params.gap,
			// the head segment cannot shrink below the min width
			maxSegmentLeftPx,
		);

		if (
			// if the incoming segment has higher severity
			severityDiff(health ?? Api.HealthStateValue.UNKNOWN, this.segment.health ?? Api.HealthStateValue.UNKNOWN) > 0 &&
			// AND the head segment can shrink
			shiftedSegmentLeftPx > this.segment.leftPx
		) {
			// the head segment shrinks to release space for the incoming more severe segment
			return new HealthSegmentNode(this.params, this.tail, {
				...this.segment,
				leftPx: shiftedSegmentLeftPx,
			}).shift(leftPx, health); // and tries the incoming segment again
		}

		// creates a new segment with the gap to the left of the head segment
		return new HealthSegmentNode(this.params, this, {
			health,
			leftPx,
			rightPx: this.segment.leftPx - this.params.gap,
		});
	}

	public [Symbol.iterator](): Iterator<HealthSegment> {
		// make sure that the first segment is handled as well
		let current = this.shift(0, undefined);

		return {
			next() {
				const child = current.next();

				if (child == null) {
					return { value: null, done: true };
				}

				current = child[1];

				return { value: child[0], done: false };
			},
		};
	}
}

class HealthSegmentEdge implements HealthSegmentBuilder {
	public constructor(
		private readonly params: HealthSegmentBuilderParams,
		private readonly rightPx: number,
	) {}

	public next(): null {
		return null;
	}

	public shift(leftPx: number, health: Api.HealthStateValue | undefined): HealthSegmentBuilder {
		return new HealthSegmentNode(
			this.params,
			this,
			// this is an initial the most right segment - it always ends at the right edge and does not have gap
			{ health, leftPx, rightPx: this.rightPx },
		);
	}

	public [Symbol.iterator](): Iterator<HealthSegment> {
		// make sure that the first segment is handled as well
		let current = this.shift(0, Api.HealthStateValue.UNKNOWN);

		return {
			next() {
				const child = current.next();

				if (child == null) {
					return { value: null, done: true };
				}

				current = child[1];

				return { value: child[0], done: false };
			},
		};
	}
}

export interface HealthChange {
	timestamp: number;
	newHealth: Api.HealthStateValue | undefined;
}

const HEALTH_STATE_TO_SEVERITY = {
	[Api.HealthStateValue.UNKNOWN]: 0,
	[Api.HealthStateValue.UNINITIALIZED]: 1,
	[Api.HealthStateValue.CLEAR]: 2,
	[Api.HealthStateValue.DISABLED]: 3,
	[Api.HealthStateValue.DEVIATING]: 4,
	[Api.HealthStateValue.FLAPPING]: 5,
	[Api.HealthStateValue.CRITICAL]: 6,
} satisfies Record<Api.HealthStateValue>;

function severityDiff(left: Api.HealthStateValue, right: Api.HealthStateValue): number {
	return HEALTH_STATE_TO_SEVERITY[left] - HEALTH_STATE_TO_SEVERITY[right];
}

export interface HealthSegment {
	health: Api.HealthStateValue | undefined;
	leftPx: number;
	rightPx: number;
}

export interface GetHealthSegmentsParams extends HealthSegmentBuilderParams {
	healthChanges: ReadonlyArray<HealthChange>;
	timelineStart: Time;
	timelineEnd: Time;
	timelineWidth: number;
}

/**
 * Quantify health segments such that each segment is at least 2x the HEALTH_LINE_GAP_PX
 * Do this by extending segments if possible (start / end shift at most 1.5 GAP_PX) or dropping them otherwise
 */
export const getHealthSegments = ({
	healthChanges,
	timelineStart,
	timelineEnd,
	timelineWidth,
	gap,
	minWidth,
}: GetHealthSegmentsParams): Array<HealthSegment> => {
	const segments = sortBy(healthChanges, [({ timestamp }) => timestamp, "desc"]).reduce(
		(builder, { timestamp, newHealth }) => {
			const leftPx = timeToPx({
				time: timestamp,
				width: timelineWidth,
				interval: { start: timelineStart, end: timelineEnd },
			});

			return builder.shift(round(leftPx, 0), newHealth);
		},
		new HealthSegmentEdge({ gap, minWidth }, timelineWidth),
	);

	return toArray(segments);
};
